import React from 'react';
import { AppBar, Toolbar, Typography, IconButton, Box, Container, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, Button } from '@mui/material';
import Verified from '@mui/icons-material/Verified';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PersonIcon from '@mui/icons-material/Person';
import logo from '../../assets/xpdlogo.png';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import { Close } from '@mui/icons-material';
import { useState } from 'react';
const Header = ({ username, onLogout, isDesktop, drawerWidth, handleDrawerToggle }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // for tablet
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // for mobile
  // Add this state at the beginning of your component
  const [openLimitsDialog, setOpenLimitsDialog] = useState(false);

  // Add this handler 
  const handleLimitsClick = () => {
    setOpenLimitsDialog(true);
  };
  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        color: "#000",
        backgroundColor: "#fff",
      }}
      elevation={0}
    >
      <Container maxWidth="100vw">
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: { xs: '56px', sm: '64px' },
            px: { xs: 1, sm: 2, md: 3 } // Responsive padding
          }}
        >
          {/* Left Section with Menu and Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isDesktop && (
              <IconButton
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  color: '#542a74',
                  mr: { xs: 0.5, sm: 1 },
                  display: { md: 'none' }
                }}
              >
                <MenuIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
              </IconButton>
            )}
            <Box
              sx={{
                width: isDesktop ? `${drawerWidth}px` : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                maxWidth: {
                  xs: '100px',  // mobile
                  sm: '120px',  // tablet
                  md: '160px'   // desktop
                }
              }}
            >
              <img
                src={logo}
                alt="My XPD"
                style={{
                  maxWidth: '100%',
                  height: isMobile ? '30px' : isTablet ? '35px' : '50px',
                  objectFit: 'contain'
                }}
              />
            </Box>
          </Box>

          {/* Middle Section with Welcome Message */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', sm: 'flex-start' },
              ml: { xs: 1, sm: 2, md: 3, lg: 5, xl: 5 },
              flexGrow: 1
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                display: { xs: 'none', sm: 'block' } // Hide on mobile
              }}
            >
              Welcome!
            </Typography>
            <Typography
              variant="h3"
              color="#7517a7"
              sx={{
                fontSize: {
                  xs: '1rem',      // mobile
                  sm: '1.25rem',   // tablet
                  md: '1.5rem'     // desktop
                },
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: {
                  xs: '120px',
                  sm: '200px',
                  md: '300px'
                }
              }}
            >
              {username}
            </Typography>
          </Box>

          {/* Right Section with Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 0.5, sm: 1, md: 2 },
              ml: { xs: 1, sm: 2 }
            }}
          >
            <Button
              onClick={handleLimitsClick}
              sx={{
                color: '#542a74',
                backgroundColor: 'transparent',
                fontSize: { xs: '12px', sm: '14px', md: '14px' },
                padding: { xs: '4px 8px', sm: '6px 12px' },
                minWidth: 'auto',
                textTransform: 'none',
                fontWeight: 500,
                border: '1px solid rgba(84, 42, 116, 0.2)',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(84, 42, 116, 0.08)',
                  border: '1px solid rgba(84, 42, 116, 0.3)',
                },
              }}
            >
              Limits
            </Button>
            <IconButton
              onClick={() => navigate('/profile')}
              sx={{
                color: '#542a74',
                padding: { xs: 0.5, sm: 1 },
                '&:hover': {
                  backgroundColor: 'rgba(84, 42, 116, 0.08)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
              aria-label="Profile"
            >
              <PersonIcon sx={{
                fontSize: {
                  xs: '20px',
                  sm: '22px',
                  md: '24px'
                }
              }} />
            </IconButton>
            <IconButton
              onClick={onLogout}
              sx={{
                color: '#542a74',
                padding: { xs: 0.5, sm: 1 }
              }}
            >
              <PowerSettingsNewIcon sx={{
                fontSize: {
                  xs: '20px',
                  sm: '22px',
                  md: '24px'
                }
              }} />
            </IconButton>
          </Box>
        </Toolbar>
        {/* Limits Dialog */}
        <Dialog
          open={openLimitsDialog}
          onClose={() => setOpenLimitsDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            }
          }}
        >
          <DialogTitle
            sx={{
              color: '#542a74',
              fontSize: { xs: '18px', sm: '20px' },
              fontWeight: 600,
              pb: 1,
              pt: 2,
              px: 3,
              textAlign: 'center', // Center the text
              display: 'flex',
              justifyContent: 'center', // Center the content
              alignItems: 'center',
              position: 'relative', // For proper positioning with close button
              '& .MuiTypography-root': { // Target Typography if DialogTitle uses it internally
                textAlign: 'center',
                width: '100%'
              }
            }}
          >
            Spending Limits
          </DialogTitle>
          <IconButton
            onClick={() => setOpenLimitsDialog(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#542a74',
            }}
          >
            <Close fontSize="small" />
          </IconButton>
          <DialogContent sx={{ px: 3, py: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {[
                { amount: '$10,000', period: 'Maximum amount per transaction' },
                { amount: '$15,000', period: 'Daily spend limit' },
                { amount: '$40,000', period: 'Monthly spend limit' },
                { amount: '$200,000', period: 'Yearly spend limit' },
              ].map((limit, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(84, 42, 116, 0.04)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(84, 42, 116, 0.08)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#542a74',
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: { xs: '16px', sm: '18px' },
                          fontWeight: 600,
                          color: '#542a74',
                          mb: 0.5,
                        }}
                      >
                        {limit.amount}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: { xs: '12px', sm: '14px' },
                        }}
                      >
                        {limit.period}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </DialogContent>
        </Dialog>
      </Container>
    </AppBar>
  );
};

export default Header;
